// src/components/ClinicalHistoryWizard/ClinicalHistoryWizard.js
// Mentfull — Reconstrucción del Wizard de Historial Clínico (con storytelling UX)
// Optimizado para: rendimiento, validación, guardado incremental, reanudación y guía narrativa
// Requiere: antd, firebase v9 modular

import React, { useEffect, useMemo, useState } from "react";
import "./ClinicalHistoryWizard.scss";
import {
  Steps,
  Form,
  Input,
  Radio,
  Checkbox,
  Button,
  Select,
  message,
  Tooltip,
  Progress,
  Alert,
  Collapse,
  Divider,
} from "antd";
import { getAuth } from "firebase/auth";
import {
  getFirestore,
  doc,
  getDoc,
  setDoc,
  serverTimestamp,
} from "firebase/firestore";

/** ============================
 * UTILIDADES
 * ============================ */
const cm = (n) => (n ? `${n} cm` : "");
const kg = (n) => (n ? `${n} kg` : "");
const mtr = (n) => (n ? `${n} m` : "");

const BOOL = [
  { label: "Sí", value: true },
  { label: "No", value: false },
];
const NUNCA_CASI_SIEMPRE = [
  "nunca",
  "rara vez",
  "a veces",
  "frecuentemente",
  "siempre",
];
const SI_NO_TXT = [
  { label: "sí", value: "sí" },
  { label: "no", value: "no" },
];

const loadDraft = (uid) => {
  try {
    return JSON.parse(localStorage.getItem(`mf_clinical_draft_${uid}`) || "{}");
  } catch {
    return {};
  }
};
const saveDraft = (uid, data) => {
  try {
    localStorage.setItem(`mf_clinical_draft_${uid}`, JSON.stringify(data));
  } catch {}
};
const clearDraft = (uid) => {
  try {
    localStorage.removeItem(`mf_clinical_draft_${uid}`);
  } catch {}
};

/** ============================
 * CAMPOS (llaves EXACTAS a Firestore)
 * ============================ */
const defaultValues = {
  // Datos complementarios
  ocupacion: "",
  estadoCivil: "",
  objetivo: "",

  // Antecedentes
  tabaco: "nunca",
  drogas: "nunca",
  alcohol: "nunca",
  cafe: "nunca",
  problemasPresion: "no",
  problemasCardio: "no",
  padecesEnfermedad: "no",
  detalleEnfermedades: "",
  alergias: "no",
  suplementos: "no",
  medicamento: "",
  dosis: "",
  desdeCuando: "",
  diabetes: false,
  obesidad: false,
  hta: false,
  hipercolesterolemia: false,
  hipertrigliceridemia: false,
  cancer: false,

  // Indicadores
  malestaresClinicos: [],
  apetito: "",
  horaHambre: "",
  alimentosFavoritos: "",
  alimentosMalestar: "",
  alimentosNoTeGustan: "",
  alimentosAlergico: "",

  // Lifestyle
  deportesPracticas: "",
  diasSemanaPractica: "",
  horasDiaPractica: "",
  tiempoPractica: "",
  desayuno: "",
  colacionMatutina: "",
  comida: "",
  colacionVespertina: "",
  cena: "",
  horasDuermes: "",
  litrosAgua: "",
  estiloVidaSaludable: "",
  habitoPerjudicial: "",

  // Medidas
  peso: "",
  talla: "",
  estatura: "",
  brazo: "",
  cintura: "",
  pierna: "",
  cadera: "",
  fuerza: "",
  resistencia: "",
  movilidad: "",
  flexibilidad: "",
};

const ALL_KEYS = Object.keys(defaultValues);

/** ============================
 * META DE STORYTELLING POR PASO
 * ============================ */
const STEP_META = [
  {
    emoji: "👋",
    title: "Datos complementarios",
    subtitle: "Tu punto de partida",
    why: "Con esto personalizamos el tono y el enfoque de tu plan.",
  },
  {
    emoji: "🩺",
    title: "Antecedentes de salud",
    subtitle: "Contexto médico",
    why: "Nos permite ajustar cargas, intensidades y restricciones seguras.",
  },
  {
    emoji: "🧭",
    title: "Indicadores y hábitos",
    subtitle: "Tu relación con la comida",
    why: "Detectamos detonantes y preferencias reales para que la dieta sea disfrutable.",
  },
  {
    emoji: "⚡",
    title: "Estilo de vida",
    subtitle: "Movimiento y descanso",
    why: "Alineamos horarios, energía y tiempos disponibles para maximizar adherencia.",
  },
  {
    emoji: "📏",
    title: "Medidas corporales",
    subtitle: "Tu línea base",
    why: "Con estas métricas daremos seguimiento a tus avances.",
  },
];

const AFFIRMATIONS = [
  "¡Vas excelente!",
  "Un paso más y listo ✨",
  "Esto tomará solo unos minutos ⏱️",
  "Gracias por confiar en Mentfull 💚",
];

/** ============================
 * SUB-COMPONENTES DE STORYTELLING
 * ============================ */
function StoryHeader({ name, percent }) {
  return (
    <div className="story-header">
      <div className="title">
        <h2>Hola{name ? `, ${name}` : ""} 👋</h2>
        <p>
          Creemos tu plan personalizado en <strong>5 pasos</strong>. Tardarás
          ~5–7 min.
        </p>
      </div>
      <div className="progress">
        <Progress percent={percent} size="small" />
        <span className="affirmation">
          {AFFIRMATIONS[percent % AFFIRMATIONS.length]}
        </span>
      </div>
    </div>
  );
}

function StepIntro({ meta }) {
  return (
    <div className="step-intro">
      <div className="emoji">{meta.emoji}</div>
      <div>
        <h3>{meta.title}</h3>
        <p className="subtitle">{meta.subtitle}</p>
        <p className="why">
          <span>¿Por qué lo pedimos?</span> {meta.why}
        </p>
      </div>
    </div>
  );
}

function PrivacyBadge() {
  return (
    <div className="privacy-badge">
      🔒 Tus datos se usan solo para tu plan y puedes editarlos en cualquier
      momento.
    </div>
  );
}

/** ============================
 * WIZARD
 * ============================ */
export default function ClinicalHistoryWizard({ onComplete, userName }) {
  const [form] = Form.useForm();
  const auth = getAuth();
  const db = getFirestore();
  const user = auth.currentUser;

  const [current, setCurrent] = useState(0);
  const [loading, setLoading] = useState(false);
  const uid = user?.uid || "local";

  // Precarga: Firestore + draft
  useEffect(() => {
    (async () => {
      const draft = loadDraft(uid);
      let cloud = {};
      if (user) {
        const ref = doc(db, "clinicalHistory", user.uid);
        const snap = await getDoc(ref);
        if (snap.exists()) cloud = snap.data();
      }
      const initial = { ...defaultValues, ...cloud, ...draft };
      form.setFieldsValue(initial);
    })();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [uid]);

  // Guardado incremental (local)
  const watchAll = Form.useWatch([], form);
  useEffect(() => {
    if (watchAll) saveDraft(uid, watchAll);
  }, [uid, watchAll]);

  // % de avance en base a campos llenos
  const percent = (() => {
    const vals = form.getFieldsValue(true);
    const filled = ALL_KEYS.filter((k) => {
      const v = vals[k];
      if (Array.isArray(v)) return v.length > 0;
      if (typeof v === "boolean") return true; // mostrado => considerado
      return v !== undefined && v !== null && String(v).trim() !== "";
    }).length;
    return Math.round((filled / ALL_KEYS.length) * 100);
  })();

  const watchPadece = Form.useWatch("padecesEnfermedad", form);
  const watchSuplementos = Form.useWatch("suplementos", form);

  const steps = useMemo(
    () => [
      {
        title: "Datos",
        description: "Complementarios",
        content: (
          <>
            <StepIntro meta={STEP_META[0]} />
            <section className="step-grid">
              <Form.Item
                name="ocupacion"
                label={
                  <LabelWithHint
                    text="Ocupación"
                    hint="Lo que haces la mayor parte del día."
                  />
                }
                rules={[{ required: true, message: "Ingresa tu ocupación" }]}
              >
                <Input placeholder="Ej. Desarrollador" allowClear />
              </Form.Item>
              <Form.Item
                name="estadoCivil"
                label={
                  <LabelWithHint
                    text="Estado Civil"
                    hint="Nos ayuda a entender rutinas y horarios."
                  />
                }
                rules={[{ required: true }]}
              >
                <Select
                  options={[
                    "Soltero",
                    "Casado",
                    "Unión libre",
                    "Divorciado",
                    "Viudo",
                  ].map((v) => ({ label: v, value: v }))}
                />
              </Form.Item>
              <Form.Item
                name="objetivo"
                label={
                  <LabelWithHint
                    text="Objetivo"
                    hint="¿Qué te gustaría lograr?"
                  />
                }
                rules={[{ required: true }]}
              >
                <Input placeholder="Ej. Estar Sano" allowClear />
              </Form.Item>
            </section>
          </>
        ),
      },
      {
        title: "Antecedentes",
        content: (
          <>
            <StepIntro meta={STEP_META[1]} />

            {/* Grupo: Hábitos de consumo */}
            <GroupTitle
              title="Hábitos de consumo"
              desc="Contesta según tu frecuencia habitual."
            />
            <section className="step-grid">
              <Form.Item
                name="tabaco"
                label={
                  <LabelWithHint
                    text="Consumo de tabaco"
                    hint="Frecuencia habitual"
                  />
                }
                rules={[{ required: true }]}
              >
                <Select
                  options={NUNCA_CASI_SIEMPRE.map((v) => ({
                    label: v,
                    value: v,
                  }))}
                />
              </Form.Item>
              <Form.Item
                name="drogas"
                label={
                  <LabelWithHint
                    text="Consumo de drogas"
                    hint="Si no aplica, elige 'nunca'"
                  />
                }
                rules={[{ required: true }]}
              >
                <Select
                  options={NUNCA_CASI_SIEMPRE.map((v) => ({
                    label: v,
                    value: v,
                  }))}
                />
              </Form.Item>
              <Form.Item
                name="alcohol"
                label={
                  <LabelWithHint
                    text="Consumo de alcohol"
                    hint="Incluye fines de semana"
                  />
                }
                rules={[{ required: true }]}
              >
                <Select
                  options={NUNCA_CASI_SIEMPRE.map((v) => ({
                    label: v,
                    value: v,
                  }))}
                />
              </Form.Item>
              <Form.Item
                name="cafe"
                label={
                  <LabelWithHint
                    text="Consumo de café"
                    hint="Tazas por día/semana"
                  />
                }
                rules={[{ required: true }]}
              >
                <Select
                  options={NUNCA_CASI_SIEMPRE.map((v) => ({
                    label: v,
                    value: v,
                  }))}
                />
              </Form.Item>
            </section>

            {/* Grupo: Condiciones generales */}
            <GroupTitle
              title="Condiciones generales"
              desc="Indica si presentas alguna de las siguientes."
            />
            <section className="step-grid">
              <Form.Item
                name="problemasPresion"
                label="Problemas de presión"
                rules={[{ required: true }]}
              >
                <Radio.Group options={SI_NO_TXT} />
              </Form.Item>
              <Form.Item
                name="problemasCardio"
                label="Problemas cardiovasculares"
                rules={[{ required: true }]}
              >
                <Radio.Group options={SI_NO_TXT} />
              </Form.Item>
              <Form.Item
                name="padecesEnfermedad"
                label="¿Padeces alguna enfermedad?"
                rules={[{ required: true }]}
              >
                <Radio.Group options={SI_NO_TXT} />
              </Form.Item>
              {watchPadece === "sí" && (
                <Form.Item
                  name="detalleEnfermedades"
                  label={
                    <LabelWithHint
                      text="Detalles de enfermedades"
                      hint="Especifica nombre, diagnóstico y estado actual"
                    />
                  }
                >
                  <Input.TextArea
                    rows={2}
                    maxLength={400}
                    showCount
                    allowClear
                  />
                </Form.Item>
              )}
            </section>

            {/* Grupo: Alergias y suplementos */}
            <GroupTitle
              title="Alergias y suplementos"
              desc="Solo si aplican a tu caso actual."
            />
            <section className="step-grid">
              <Form.Item
                name="alergias"
                label="Alergias"
                rules={[{ required: true }]}
              >
                <Input placeholder="Ej. Ninguna" allowClear />
              </Form.Item>
              <Form.Item
                name="suplementos"
                label="¿Suplementos o medicamentos?"
                rules={[{ required: true }]}
              >
                <Radio.Group options={SI_NO_TXT} />
              </Form.Item>
              {watchSuplementos === "sí" && (
                <>
                  <Form.Item name="medicamento" label="Medicamento">
                    <Input allowClear />
                  </Form.Item>
                  <Form.Item name="dosis" label="Dosis">
                    <Input allowClear />
                  </Form.Item>
                  <Form.Item name="desdeCuando" label="Desde cuándo">
                    <Input allowClear />
                  </Form.Item>
                </>
              )}
            </section>

            {/* Grupo: Padecimientos/condiciones específicas */}
            <GroupTitle
              title="Padecimientos o condiciones"
              desc="De los siguientes padecimientos o condiciones, ¿hay alguno que poseas?"
            />
            <section className="step-grid">
              <Form.Item name="diabetes" label="Diabetes">
                <Radio.Group
                  options={BOOL}
                  optionType="button"
                  buttonStyle="solid"
                />
              </Form.Item>
              <Form.Item name="obesidad" label="Obesidad">
                <Radio.Group
                  options={BOOL}
                  optionType="button"
                  buttonStyle="solid"
                />
              </Form.Item>
              <Form.Item name="hta" label="HTA">
                <Radio.Group
                  options={BOOL}
                  optionType="button"
                  buttonStyle="solid"
                />
              </Form.Item>
              <Form.Item name="hipercolesterolemia" label="Hipercolesterolemia">
                <Radio.Group
                  options={BOOL}
                  optionType="button"
                  buttonStyle="solid"
                />
              </Form.Item>
              <Form.Item
                name="hipertrigliceridemia"
                label="Hipertrigliceridemia"
              >
                <Radio.Group
                  options={BOOL}
                  optionType="button"
                  buttonStyle="solid"
                />
              </Form.Item>
              <Form.Item name="cancer" label="Cáncer">
                <Radio.Group
                  options={BOOL}
                  optionType="button"
                  buttonStyle="solid"
                />
              </Form.Item>
            </section>
          </>
        ),
      },
      {
        title: "Indicadores",
        content: (
          <>
            <StepIntro meta={STEP_META[2]} />

            <GroupTitle
              title="Señales de tu cuerpo"
              desc="Para detectar detonantes y organizar tus tiempos de comida."
            />
            <section className="step-grid">
              <Form.Item
                name="malestaresClinicos"
                label={
                  <LabelWithHint
                    text="Malestares clínicos"
                    hint="Marca los que apliquen"
                  />
                }
              >
                <Checkbox.Group
                  options={[
                    "Dolor de cabeza",
                    "Acidez",
                    "Gases",
                    "Estreñimiento",
                    "Diarrea",
                    "Insomnio",
                    "Ansiedad",
                    "Estrés",
                  ]}
                />
              </Form.Item>
              <Form.Item
                name="apetito"
                label={
                  <LabelWithHint text="Apetito" hint="Percepción general" />
                }
                rules={[{ required: true }]}
              >
                <Select
                  options={["Malo", "Regular", "Bueno", "Excelente"].map(
                    (v) => ({ label: v, value: v })
                  )}
                />
              </Form.Item>
              <Form.Item
                name="horaHambre"
                label={
                  <LabelWithHint
                    text="Hora de mayor hambre"
                    hint="Ej. Mañanas/Tardes/Noche"
                  />
                }
                rules={[{ required: true }]}
              >
                <Input placeholder="Ej. En las tardes" allowClear />
              </Form.Item>
            </section>

            <GroupTitle
              title="Preferencias"
              desc="Lo que te gusta y lo que evitarías."
            />
            <section className="step-grid">
              <Form.Item
                name="alimentosFavoritos"
                label="Alimentos favoritos"
                rules={[{ required: true }]}
              >
                <Input placeholder="Ej. Chile relleno, frijoles…" allowClear />
              </Form.Item>
              <Form.Item
                name="alimentosNoTeGustan"
                label="No te gustan"
                rules={[{ required: true }]}
              >
                <Input placeholder="Ej. Lechuga, pescado…" allowClear />
              </Form.Item>
            </section>

            <GroupTitle
              title="Sensibilidades"
              desc="Ingredientes que te caen mal o que te dan reacción."
            />
            <section className="step-grid">
              <Form.Item
                name="alimentosMalestar"
                label="Causan malestar"
                rules={[{ required: true }]}
              >
                <Input placeholder="Ej. Lácteos, picantes…" allowClear />
              </Form.Item>
              <Form.Item name="alimentosAlergico" label="Alergias alimentarias">
                <Input placeholder="Ej. Ninguna" allowClear />
              </Form.Item>
            </section>
          </>
        ),
      },
      {
        title: "Lifestyle",
        content: (
          <>
            <StepIntro meta={STEP_META[3]} />

            <GroupTitle
              title="Actividad física"
              desc="Cuéntanos cuánto te mueves a la semana."
            />
            <section className="step-grid">
              <Form.Item
                name="deportesPracticas"
                label="Deportes que practicas"
                rules={[{ required: true }]}
              >
                <Input placeholder="Ej. Judo" allowClear />
              </Form.Item>
              <Form.Item
                name="diasSemanaPractica"
                label="Días de práctica"
                rules={[{ required: true }]}
              >
                <Input placeholder="Ej. 3 días, 2 h cada día" allowClear />
              </Form.Item>
              <Form.Item
                name="horasDiaPractica"
                label="Horas diarias de práctica"
                rules={[{ required: true }]}
              >
                <Input placeholder="Ej. 2 h máximo" allowClear />
              </Form.Item>
              <Form.Item
                name="tiempoPractica"
                label="Tiempo practicando"
                rules={[{ required: true }]}
              >
                <Input placeholder="Ej. desde hace 8 años" allowClear />
              </Form.Item>
            </section>

            <GroupTitle
              title="Alimentación diaria"
              desc="Qué sueles comer en un día típico."
            />
            <section className="step-grid">
              <Form.Item
                name="desayuno"
                label="Desayuno"
                rules={[{ required: true }]}
              >
                <Input
                  placeholder="Ej. Huevos con verduras y pan integral"
                  allowClear
                />
              </Form.Item>
              <Form.Item
                name="colacionMatutina"
                label="Colación matutina"
                rules={[{ required: true }]}
              >
                <Input placeholder="Ej. Fruta o yogurt" allowClear />
              </Form.Item>
              <Form.Item
                name="comida"
                label="Comida"
                rules={[{ required: true }]}
              >
                <Input placeholder="Ej. Pollo, arroz y ensalada" allowClear />
              </Form.Item>
              <Form.Item
                name="colacionVespertina"
                label="Colación vespertina"
                rules={[{ required: true }]}
              >
                <Input placeholder="Ej. Frutos secos" allowClear />
              </Form.Item>
              <Form.Item name="cena" label="Cena" rules={[{ required: true }]}>
                <Input placeholder="Ej. Ensalada con proteína" allowClear />
              </Form.Item>
            </section>

            <GroupTitle
              title="Sueño e hidratación"
              desc="Hábitos que impactan tu energía y recuperación."
            />
            <section className="step-grid">
              <Form.Item
                name="horasDuermes"
                label="Horas que duermes"
                rules={[{ required: true }]}
              >
                <Input placeholder="Ej. 6 horas" allowClear />
              </Form.Item>
              <Form.Item
                name="litrosAgua"
                label="Litros de agua diarios"
                rules={[{ required: true }]}
              >
                <Input placeholder="Ej. 2–3 l" allowClear />
              </Form.Item>
              <Form.Item
                name="estiloVidaSaludable"
                label="¿Estilo de vida saludable?"
                rules={[{ required: true }]}
              >
                <Radio.Group
                  options={[
                    { label: "Si", value: "Si" },
                    { label: "No", value: "No" },
                  ]}
                />
              </Form.Item>
              <Form.Item
                name="habitoPerjudicial"
                label="¿Algún hábito perjudicial?"
                rules={[{ required: true }]}
              >
                <Radio.Group
                  options={[
                    { label: "Sí", value: "Sí" },
                    { label: "No", value: "No" },
                  ]}
                />
              </Form.Item>
            </section>
          </>
        ),
      },
      {
        title: "Medidas",
        content: (
          <>
            <StepIntro meta={STEP_META[4]} />
            <Alert
              type="info"
              showIcon
              style={{ marginBottom: 12 }}
              message="Tip: si no conoces alguna medida ahora, puedes completarla más tarde desde tu Dashboard."
            />

            <Collapse
              style={{ marginBottom: 12 }}
              items={[
                {
                  key: "g",
                  label: "Guía rápida para medir en casa",
                  children: (
                    <ul className="guide-list">
                      <li>
                        <strong>Peso:</strong> mídete por la mañana, sin zapatos
                        y con ropa ligera.
                      </li>
                      <li>
                        <strong>Estatura:</strong> descalzo, de espaldas a la
                        pared, talones juntos y mirada al frente.
                      </li>
                      <li>
                        <strong>Cintura:</strong> cinta a la altura del ombligo,
                        sin apretar; exhala y mide relajado.
                      </li>
                      <li>
                        <strong>Cadera:</strong> rodea la zona más ancha de los
                        glúteos, mantén la cinta horizontal.
                      </li>
                      <li>
                        <strong>Pierna (muslo):</strong> mide a mitad entre
                        cadera y rodilla, sin tensar.
                      </li>
                      <li>
                        <strong>Brazo:</strong> mide a mitad entre hombro y
                        codo, brazo relajado.
                      </li>
                    </ul>
                  ),
                },
              ]}
            />

            <Divider />

            <GroupTitle
              title="Introduce tus medidas"
              desc="Usa números; puedes incluir decimales (ej. 84.5)."
            />
            <section className="step-grid">
              <Form.Item
                name="peso"
                label={<LabelWithHint text="Peso" hint="Mañana, ropa ligera" />}
                rules={[
                  { required: true },
                  {
                    pattern: /^\d+(\.\d+)?$/,
                    message: "Usa solo números o decimales (p. ej., 89 o 89.5)",
                  },
                ]}
              >
                <Input
                  placeholder="Ej. 89"
                  addonAfter="kg"
                  inputMode="decimal"
                />
              </Form.Item>
              <Form.Item
                name="talla"
                label={
                  <LabelWithHint
                    text="Talla (vestimenta)"
                    hint="Número de pantalón u otra referencia"
                  />
                }
                rules={[{ required: true }]}
              >
                <Input placeholder="Ej. 34" />
              </Form.Item>
              <Form.Item
                name="estatura"
                label={
                  <LabelWithHint
                    text="Estatura"
                    hint="Descalzo, contra la pared"
                  />
                }
                rules={[
                  { required: true },
                  { pattern: /^\d+(\.\d+)?$/, message: "Ej. 1.70" },
                ]}
              >
                <Input
                  placeholder="Ej. 1.86"
                  addonAfter="m"
                  inputMode="decimal"
                />
              </Form.Item>
              <Form.Item
                name="cintura"
                label={
                  <LabelWithHint
                    text="Cintura"
                    hint="A la altura del ombligo"
                  />
                }
                rules={[
                  { required: true },
                  { pattern: /^\d+(\.\d+)?$/, message: "Ej. 84 o 84.5" },
                ]}
              >
                <Input
                  placeholder="Ej. 84"
                  addonAfter="cm"
                  inputMode="decimal"
                />
              </Form.Item>
              <Form.Item
                name="cadera"
                label={
                  <LabelWithHint
                    text="Cadera"
                    hint="Zona más ancha de glúteos"
                  />
                }
                rules={[
                  { required: true },
                  { pattern: /^\d+(\.\d+)?$/, message: "Ej. 90" },
                ]}
              >
                <Input
                  placeholder="Ej. 90"
                  addonAfter="cm"
                  inputMode="decimal"
                />
              </Form.Item>
              <Form.Item
                name="pierna"
                label={
                  <LabelWithHint
                    text="Pierna (muslo)"
                    hint="Mitad entre cadera y rodilla"
                  />
                }
                rules={[
                  { required: true },
                  { pattern: /^\d+(\.\d+)?$/, message: "Ej. 50" },
                ]}
              >
                <Input
                  placeholder="Ej. 50"
                  addonAfter="cm"
                  inputMode="decimal"
                />
              </Form.Item>
              <Form.Item
                name="brazo"
                label={
                  <LabelWithHint
                    text="Brazo"
                    hint="Mitad entre hombro y codo"
                  />
                }
                rules={[
                  { required: true },
                  { pattern: /^\d+(\.\d+)?$/, message: "Ej. 20" },
                ]}
              >
                <Input
                  placeholder="Ej. 20"
                  addonAfter="cm"
                  inputMode="decimal"
                />
              </Form.Item>
              <Form.Item
                name="fuerza"
                label={<LabelWithHint text="Fuerza" hint="Opcional" />}
              >
                <Input allowClear />
              </Form.Item>
              <Form.Item
                name="resistencia"
                label={<LabelWithHint text="Resistencia" hint="Opcional" />}
              >
                <Input allowClear />
              </Form.Item>
              <Form.Item
                name="movilidad"
                label={<LabelWithHint text="Movilidad" hint="Opcional" />}
              >
                <Input allowClear />
              </Form.Item>
              <Form.Item
                name="flexibilidad"
                label={<LabelWithHint text="Flexibilidad" hint="Opcional" />}
              >
                <Input allowClear />
              </Form.Item>
            </section>
          </>
        ),
      },
    ],
    [watchPadece, watchSuplementos]
  );

  const next = async () => {
    try {
      await form.validateFields();
      setCurrent((c) => c + 1);
    } catch (e) {}
  };
  const prev = () => setCurrent((c) => c - 1);

  const onFinish = async (values) => {
    if (!user) return message.error("Debes iniciar sesión.");
    const normalized = {
      ...values,
      peso: values.peso?.toString()?.includes("kg")
        ? values.peso
        : kg(values.peso),
      estatura: values.estatura?.toString()?.includes("m")
        ? values.estatura
        : mtr(values.estatura),
      brazo: values.brazo?.toString()?.includes("cm")
        ? values.brazo
        : cm(values.brazo),
      cintura: values.cintura?.toString()?.includes("cm")
        ? values.cintura
        : cm(values.cintura),
      pierna: values.pierna?.toString()?.includes("cm")
        ? values.pierna
        : cm(values.pierna),
      cadera: values.cadera?.toString()?.includes("cm")
        ? values.cadera
        : cm(values.cadera),
      updatedAt: serverTimestamp(),
    };

    setLoading(true);
    try {
      await setDoc(
        doc(getFirestore(), "clinicalHistory", user.uid),
        normalized,
        { merge: true }
      );
      clearDraft(uid);
      message.success("Historial clínico guardado");
      if (typeof onComplete === "function") onComplete();
    } catch (err) {
      console.error(err);
      message.error("No se pudo guardar, intenta nuevamente.");
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="clinical-wizard">
      <StoryHeader name={userName} percent={percent} />
      <PrivacyBadge />

      <Steps
        current={current}
        size="small"
        className="wizard-steps"
        items={STEP_META.map((s) => ({ title: s.title }))}
      />

      <Form
        form={form}
        layout="vertical"
        className="wizard-form"
        onFinish={onFinish}
        initialValues={defaultValues}
      >
        <div className="wizard-stage">{steps[current].content}</div>

        <div className="wizard-actions">
          {current > 0 && (
            <Button onClick={prev} className="btn-ghost">
              Atrás
            </Button>
          )}
          {current < steps.length - 1 && (
            <Button type="primary" onClick={next}>
              Siguiente
            </Button>
          )}
          {current === steps.length - 1 && (
            <Button type="primary" htmlType="submit" loading={loading}>
              Guardar Historial
            </Button>
          )}
          <Button
            onClick={() => {
              saveDraft(uid, form.getFieldsValue(true));
              message.success("Borrador guardado en este dispositivo");
            }}
          >
            Guardar borrador
          </Button>
          <Button
            danger
            onClick={() => {
              form.resetFields();
              clearDraft(uid);
            }}
          >
            Limpiar
          </Button>
        </div>
      </Form>
    </div>
  );
}

function LabelWithHint({ text, hint }) {
  return (
    <div className="label-with-hint">
      <span>{text}</span>
      {hint && (
        <Tooltip title={hint} placement="top">
          <span className="hint">ⓘ</span>
        </Tooltip>
      )}
    </div>
  );
}

function GroupTitle({ title, desc }) {
  return (
    <div className="group-title">
      <h4>{title}</h4>
      {desc && <p>— {desc}</p>}
    </div>
  );
}
